import axios from 'axios';
import { identifyUser, resetAnalytics } from './analytics/events';
import { Session, sessionActions } from './reducers/session';
import { store } from './store';
import { apiHost } from './constants';
import { getSessionId, setSessionId, clearSessionId } from './sessionHeader';

export const webApiClient = axios.create({
  baseURL: apiHost,
  headers: { 'x-api-key': (import.meta as any).env?.VITE_WEB_API_AUTH_KEY },
  withCredentials: true,
});

// Cross-site session header fallback: when the nano site is served from a different
// domain than the API (e.g. nano.fitracking.com → api.tryfi.com), browsers block
// third-party cookies. Capture the sessionId from auth responses and send it back
// via the x-session-id header that the backend already supports.
webApiClient.interceptors.response.use((response) => {
  if (response.data?.sessionId) {
    setSessionId(response.data.sessionId);
  }
  return response;
});

webApiClient.interceptors.request.use((config) => {
  const sid = getSessionId();
  if (sid) {
    config.headers['x-session-id'] = sid;
  }
  return config;
});

export async function createSessionFromExistingCredentials() {
  const response = await webApiClient.get('/auth/currentuser');
  const session: Session = {
    userId: response.data.userId,
    email: response.data.email,
  };
  identifyUser(session.userId, { email: session.email });
  store.dispatch(sessionActions.create({ session }));
}

export async function login(email: string, password: string): Promise<Session> {
  if (!email || !password) {
    throw new Error('Please provide both an email and password');
  }

  let session: Session;
  try {
    const response = await webApiClient.post('/auth/login', {
      email,
      password,
    });

    session = {
      userId: response.data.userId,
      email: response.data.email,
    };

    identifyUser(session.userId, { email: session.email });
  } catch (error) {
    if ((error as any).response?.status === 401) {
      throw new Error('Invalid email or password');
    }
    throw new Error('An unknown error occurred');
  }

  store.dispatch(sessionActions.create({ session }));
  return session;
}

export async function logout() {
  try {
    await webApiClient.post('/auth/logout');
    clearSessionId();
    store.dispatch(sessionActions.destroy({}));
    resetAnalytics();
  } catch (error) {
    throw new Error('We were unable to log you out');
  }
}

export class UserAlreadyExistsError extends Error {}

export async function signup(
  email: string,
  password: string | null,
  firstName: string,
  lastName: string | null,
  chipRegistrationStep?: boolean,
): Promise<Session> {
  if (!email || !firstName) {
    throw new Error('Please provide an email and first name');
  }

  let session: Session;
  try {
    const response = await webApiClient.post('/auth/signup', {
      email,
      password: password ?? undefined,
      firstName,
      lastName,
      // Don't identify Nano-only users in Customer.IO yet since they haven't bought a collar.
      noIdentify: true,
      nanoChipRegistration: chipRegistrationStep ?? false,
    });

    session = {
      userId: response.data.userId,
      email,
    };

    identifyUser(session.userId, { email: session.email });
  } catch (err) {
    const error = err as any;
    if (error.response.status === 409) {
      if (
        error.response.data.error &&
        error.response.data.error.code &&
        error.response.data.error.code === 'already-exists'
      ) {
        throw new UserAlreadyExistsError('That email is already in use, try logging in first');
      }
    }

    // A bad request error can be returned if the form does not pass validation (e.g. password too short, bad email)
    // and will have a corresponding message.
    if (error.response.status === 400 && error.response.data.error.message) {
      throw new Error(error.response.data.error.message);
    }

    throw new Error(`An unknown error occurred`);
  }

  store.dispatch(sessionActions.create({ session }));
  return session;
}

export async function impersonate(email: string) {
  const response = await webApiClient.post('/auth/impersonate', { email });
  const session: Session = {
    userId: response.data.userId,
    email,
    impersonating: true,
  };
  store.dispatch(sessionActions.create({ session }));
}

export async function sendPasswordResetEmail(email: string) {
  await webApiClient.post('/auth/sendpasswordreset', { email });
}
